<template>
  <div>
    <div class="page-wrapper">
      <div class="container-fluid p-0">
        <div class="row justify-content-center align-items-center min-vh-100">
          <div class="col-10 col-md-6 col-lg-4">
            <div class="text-center">
              <div>
                <div class="logo d-block mb-4">
                  <img class="img-fluid" src="../../../assets/images/logo/sfa_logo.svg" alt="SFA Logo" style="max-width: 200px;">
                </div>

                <!-- Restriction Message for Mobile Browsers -->
                <div v-if="isMobile" class="login-main">
                  <img class="img-fluid restriction-img" src="../../../assets/vectors/restrict_for_mobile.png" alt="Restricted">
                  <h4 class="text-danger fw-bold">Desktop Browser Required</h4>
                  <p class="text-muted">For the best experience, please use a desktop browser.</p><br><br>
                  <small class="mt-4 mb-0 text-center text-xs">
                    Copyright @ 2024 | All Rights Reserved. <br> Developed by LAUGFS Group IT
                  </small>
                </div>

                <!-- Show login with mobile no and password only for desktop browsers -->
                <div v-else class="login-main">
                  <form class="theme-form" @submit.prevent="handleLogin">
                    <h4 class="mb-4 text-start">Sign In</h4>
                    <!-- Mobile Number input -->
                    <div class="form-group mb-3">
                      <label class="form-label text-start w-100" for="mobileNumber">Mobile Number</label>
                      <div class="input-group">
                        <input
                            class="form-control"
                            id="mobileNumber"
                            type="tel"
                            placeholder="0771234567"
                            v-model="user.mobile.value"
                            @input="filterMobileInput"
                            maxlength="10"
                            required
                        >
                      </div>
                    </div>

                    <!-- Password input -->
                    <div class="form-group mb-3">
                      <label class="form-label text-start w-100" for="password">Password</label>
                      <div class="input-group">
                        <input
                            class="form-control"
                            id="password"
                            :type="showPassword ? 'text' : 'password'"
                            placeholder="*********"
                            v-model="user.password.value"
                            required
                        >

                        <button type="button" class="btn" @click="toggleShowPassword">
                          <i :class="showPassword ? 'fa fa-regular fa-eye' : 'fa fa-regular fa-eye-slash'"></i>
                        </button>
                      </div>
                    </div>

                    <div class="d-flex align-items-center justify-content-between">
                      <div class="form-check">
                        <input
                          class="form-check-input"
                          id="checkbox1"
                          type="checkbox"
                          v-model="rememberMe"
                          @change="handleRememberMe"
                        />
                        <label class="text-muted" for="checkbox1">Remember Me</label>
                      </div>
                      <router-link class="link text-black-50" to="/request-otp">Forgot Password?</router-link>
                    </div>
                    <br>

                    <!-- Login Button -->
                    <div class="d-grid">
                      <button class="btn button-default btn-block w-100" :disabled="loading">
                        <span v-if="loading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        <span v-else>Login</span>
                      </button>
                    </div>
                    <br>

                    <!-- Copyright widget -->
                    <small class="mt-4 mb-0 text-center text-xs">
                      Copyright &copy; 2024. All Rights Reserved. <br> Developed by LAUGFS Group IT
                    </small>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { webUserLogin } from '@/services/dataService';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';

export default {
  data() {
    return {
      showPassword: false,
      rememberMe: false, // New data property for Remember Me checkbox
      isMobile: false,
      user: {
        mobile: {
          value: '',
          errormsg: ''
        },
        password: {
          value: '',
          errormsg: ''
        },
        loginError: '' // Add this line for login error message
      },
      token: '', // Store token here
      loading: false // Add loading state
    };
  },

  setup() {
    const router = useRouter();
    const toast = useToast(); // Initialize the toaster
    return { router, toast };
  },

  mounted() {
    this.detectMobile();

    // Check if the mobile number is saved in localStorage
    const savedMobile = localStorage.getItem('mobileNumber');
    if (savedMobile) {
      this.user.mobile.value = savedMobile;
      this.rememberMe = true; // Set the checkbox state to checked
    }
  },

  methods: {
    //Detecting Mobile Browsers
    detectMobile() {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      if (/android|iphone|ipad|ipod|blackberry|windows phone/i.test(userAgent)) {
        this.isMobile = true;
      }
    },

    async handleLogin() {
      this.user.loginError = ''; // Clear previous login error message

      if (!this.user.password.value || this.user.password.value.length < 6) {
        this.user.password.errormsg = 'Minimum length 6';
        this.toast.error('Password must be at least 6 characters.'); // Show toast for password error
        return;
      } else {
        this.user.password.errormsg = '';
      }

      if (!this.user.mobile.value) {
        this.user.mobile.errormsg = 'Mobile number required';
        this.toast.error('Mobile number is required'); // Show toast for mobile number error
        return;
      } else if (!this.validMobile(this.user.mobile.value)) {
        this.user.mobile.errormsg = 'Valid 10-digit mobile number required.';
        this.toast.error('Please enter a valid 10-digit mobile number'); // Show toast for invalid mobile number
        return;
      } else {
        this.user.mobile.errormsg = '';
      }

      this.loading = true; // Set loading to true when login starts

      try {
        // User login API call
        const userResponse = await webUserLogin(this.user.mobile.value, this.user.password.value);

        // Handle successful login
        if (userResponse.data.data.temp_login === '1') {  //This condition is valid for new users.
          localStorage.setItem('token_set', userResponse.data.data.token); // Store token
          localStorage.setItem('user_id', userResponse.data.data.user_id); // Store userId
          localStorage.setItem('temp_password', this.user.password.value);
          this.toast.success("Please create password to secure your account.");
          this.$router.push('/set-password');

        } else if (userResponse.data.data.temp_login === '0') {  //This condition is valid for regular users
          localStorage.setItem('User', JSON.stringify(userResponse.data.data));
          localStorage.setItem('first_name', userResponse.data.data.first_name);
          localStorage.setItem('last_name', userResponse.data.data.last_name);
          localStorage.setItem('full_name', userResponse.data.data.full_name);
          localStorage.setItem('business_name', userResponse.data.data.business_name);
          localStorage.setItem('user_label', userResponse.data.data.user_label);
          localStorage.setItem('mobile', userResponse.data.data.mobile);
          localStorage.setItem('email', userResponse.data.data.email);
          localStorage.setItem('birthday', userResponse.data.data.dob);
          localStorage.setItem('company_id', userResponse.data.data.company_id);
          localStorage.setItem('company_name', userResponse.data.data.company_name);
          localStorage.setItem('address', userResponse.data.data.address1);
          localStorage.setItem('city', userResponse.data.data.city);
          localStorage.setItem('district_id', userResponse.data.data.district_id);
          localStorage.setItem('district', userResponse.data.data.district);
          localStorage.setItem('province', userResponse.data.data.province);
          localStorage.setItem('reportingL1Label', userResponse.data.data.reporting_label);
          localStorage.setItem('reportingL1FirstName', userResponse.data.data.reporting_first_name);
          localStorage.setItem('reportingL1LastName', userResponse.data.data.reporting_last_name);
          localStorage.setItem('reportingL1ProfileId', userResponse.data.data.reporting_profile_id);
          localStorage.setItem('reportingL1Contact', userResponse.data.data.reporting_mobile);
          localStorage.setItem('reportingL2Label', userResponse.data.data.reporting_l2_label);
          localStorage.setItem('reportingL2FirstName', userResponse.data.data.reporting_l2_first_name);
          localStorage.setItem('reportingL2LastName', userResponse.data.data.reporting_l2_last_name);
          localStorage.setItem('reportingL2ProfileId', userResponse.data.data.reporting_l2_profile_id);
          localStorage.setItem('reportingL2Contact', userResponse.data.data.reporting_l2_mobile);
          localStorage.setItem('profile_id', userResponse.data.data.profile_id);
          localStorage.setItem('user_id', userResponse.data.data.user_id);
          localStorage.setItem('profile_name', userResponse.data.data.profile_name);
          localStorage.setItem('plant_id', userResponse.data.data.plant_id ?? 0);
          localStorage.setItem('distributor_vat_code', userResponse.data.data.distributor_vat_code || '');
          localStorage.setItem('token_set', userResponse.data.data.token);

          this.toast.success(userResponse.data.message);
          this.$router.push('/dashboard');
        } else if(userResponse.data.data.password_expired === 1) { //This condition is valid for if user's password was expired. Password validity time is 45 days.
          localStorage.setItem('user_id', userResponse.data.data.user_id); // Store userId
          localStorage.setItem('token_set', userResponse.data.data.token); // Store token
          this.toast.error(userResponse.data.message);
          this.$router.push('/change-password');
        }
      } catch (error) {
        // Handle invalid credentials
        if (error.response && error.response.data && error.response.data.message) {
          this.user.loginError = error.response.data.message;
          this.toast.error(this.user.loginError); // Show toaster for login error
        } else {
          this.user.loginError = "An error occurred. Please try again.";
          this.toast.error(this.user.loginError); // Show toaster for general error
        }
        console.error('API Error:', error);
      } finally {
        this.loading = false; // Set loading to false when login completes
      }
    },

    validMobile(mobile) {
      const re = /^\d{10}$/; // Regex for 10-digit mobile number
      return re.test(mobile);
    },

    filterMobileInput(event) {
      const value = event.target.value;
      this.user.mobile.value = value.replace(/\D/g, ''); // Remove non-numeric characters
    },

    toggleShowPassword() {
      this.showPassword = !this.showPassword;
    },

    handleRememberMe() {
      // Store the mobile number in localStorage if "Remember Me" is checked
      if (this.rememberMe) {
        localStorage.setItem('mobileNumber', this.user.mobile.value);
      } else {
        localStorage.removeItem('mobileNumber');
      }
    }
  }
};
</script>

<style scoped>
.page-wrapper {
  background-color: #f8f9fa;
}

.login-main {
  background-color: #fff;
  padding: 30px 40px; /* 30px top/bottom padding, 40px left/right padding */
  border-radius: 20px; /* Adjust the border radius as needed */
}

.text-muted {
  color: #6c757d;
}

.form-label {
  text-align: left;
}

.input-group .btn {
  display: flex;
  align-items: center;
  justify-content: center;
}

i {
  font-size: 1.2rem;
  color: #000;
}

.btn {
  border: 1px solid #efefef;
  background-color: #fff;
}

.btn:hover {
  border: 1px solid #efefef;
  background-color: #fff;
}

a {
  text-decoration: none;
}

.text-muted {
  font-weight: lighter;
}

.mobile-restriction {
  margin-top: 50px;
  padding: 20px;
}

.restriction-img {
  max-width: 180px;
  margin-bottom: 15px;
}

h4 {
  font-size: 1.25rem;
}

p {
  font-size: 0.9rem;
}

/* Unchecked state */
.form-check-input {
  border: 2px solid #ccc;
  background-color: #fff;
  cursor: pointer;
}

/* Checked state */
.form-check-input:checked {
  background-color: #253069;
  border-color: #253069;
}

/* Remove the default after pseudo-element styling */
.form-check-input::after {
  display: none;
}

/* Optional: Add focus state for better accessibility */
.form-check-input:focus {
  box-shadow: 0 0 0 0.2rem rgba(37, 48, 105, 0.25);
  border-color: #253069;
}

/* Forgot Password link hover effect */
.link:hover {
  color: #253069 !important;
  text-decoration: none;
}
</style>